import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { WebViewerInstance } from '@pdftron/webviewer';
import { annotationsApi } from '../../services/api';
import { createAnnotationSaveQueue, type AnnotationSaveState } from './annotation-save-queue';

type SaveQueue = ReturnType<typeof createAnnotationSaveQueue>;
type Draft = { xfdf?: string; queue: SaveQueue };
type Session = {
  capture: () => Promise<string>;
  queue: SaveQueue;
  document: NonNullable<ReturnType<WebViewerInstance['Core']['documentViewer']['getDocument']>>;
};
export type DocumentSaveStatus = AnnotationSaveState['status'] | 'loading' | 'load-error' | 'legacy-readonly' | 'annotations-load-error';

/** Own the complete document/annotation lifetime. Exports and loads share a
 * barrier, so a late old-document callback can never read the new document. */
export function usePdfDocument({ instance, material, userId, getToken, readAlongHighlight, onLoadingChange, onReady, onBeforeLoad }: {
  instance: WebViewerInstance | null;
  material: { id: string; filename: string; file_type: string } | null;
  userId: string | null | undefined;
  getToken: () => Promise<string | null>;
  readAlongHighlight: MutableRefObject<unknown>;
  onLoadingChange: (loading: boolean) => void;
  onReady: (instance: WebViewerInstance) => void;
  /** Bind the host reader source before the SDK emits documentLoaded. */
  onBeforeLoad?: (source: { id: string; owner: string }) => void;
}) {
  const [status, setStatus] = useState<DocumentSaveStatus>('loading');
  const [reload, setReload] = useState(0);
  const ownerRef = useRef(userId);
  const tokenRef = useRef(getToken);
  const materialRef = useRef(material);
  const onReadyRef = useRef(onReady);
  const onBeforeLoadRef = useRef(onBeforeLoad);
  const loadBarrier = useRef<Promise<unknown>>(Promise.resolve());
  const sessionRef = useRef<Session | null>(null);
  const draftsRef = useRef(new Map<string, Draft>());
  const pendingExports = useRef(0);
  const blockedSnapshot = useRef<{ owner: string; capture: () => Promise<string>; queue: SaveQueue } | null>(null);
  const mounted = useRef(true);
  ownerRef.current = userId;
  tokenRef.current = getToken;
  materialRef.current = material;
  onReadyRef.current = onReady;
  onBeforeLoadRef.current = onBeforeLoad;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const source = materialRef.current;
    if (!instance || !source || !userId) return;
    const { documentViewer, annotationManager } = instance.Core;
    const owner = userId;
    const key = `${owner}\0${source.id}`;
    const abort = new AbortController();
    // A replaced source must not wait for an obsolete request whose transport
    // ignores AbortSignal. Only network waits may leave the shared SDK barrier;
    // actual document loads/imports/exports still finish before replacement.
    const aborted = new Promise<never>((_resolve, reject) => {
      abort.signal.addEventListener('abort', () => reject(new DOMException('Document replaced', 'AbortError')), { once: true });
    });
    void aborted.catch(() => {});
    const waitForRequest = <T,>(request: Promise<T>) => Promise.race([request, aborted]);
    let active = true;
    let ready = false;
    let saveHandler: (() => void) | null = null;
    let exports = Promise.resolve();
    let queue: SaveQueue | null = null;
    let capturesPending = 0;
    let exportFailed = false;
    let captureForRecovery: (() => Promise<string>) | null = null;
    const current = () => active && mounted.current && ownerRef.current === owner && materialRef.current?.id === source.id;
    const tokenForOwner = async () => {
      if (!mounted.current || ownerRef.current !== owner) throw new Error('Annotation owner changed');
      const token = await tokenRef.current();
      if (!token || !mounted.current || ownerRef.current !== owner) throw new Error('Annotation authentication unavailable');
      return token;
    };
    setStatus('loading');
    annotationManager.enableReadOnlyMode();
    onLoadingChange(true);
    sessionRef.current = null;

    // Fetching may overlap an old save, but loading/importing into the shared
    // Apryse instance must wait until the old snapshot exports have finished.
    const bytes = tokenForOwner().then(token => fetch(`/api/pdf/${encodeURIComponent(source.id)}`, {
      headers: { Authorization: `Bearer ${token}` }, signal: abort.signal,
    })).then(response => {
      if (!response.ok) throw new Error(`PDF request failed: ${response.status}`);
      return response.blob();
    });
    void bytes.catch(() => {});

    const load = loadBarrier.current.catch(() => {}).then(async () => {
      if (!current()) return;
      annotationManager.enableReadOnlyMode();
      const blocked = blockedSnapshot.current;
      if (blocked) {
        if (blocked.owner === owner) {
          // Keep the previous PDF in memory until its failed snapshot can be
          // captured. A retry must not replace the only remaining edited copy.
          await blocked.capture();
          if (!current()) return;
        }
        blocked.queue.detach();
        blockedSnapshot.current = null;
      }
      const blob = await waitForRequest(bytes);
      if (!current()) return;
      onBeforeLoadRef.current?.({ id: source.id, owner });
      // The pinned Production UI returns void from loadDocument even though
      // newer SDK typings advertise a Promise. Wait for its real load event;
      // otherwise annotation readiness can resolve against the previous PDF.
      await new Promise<void>((resolve, reject) => {
        const remove = () => {
          documentViewer.removeEventListener('documentLoaded', loaded);
          instance.UI.removeEventListener?.('loaderror', failed);
        };
        const loaded = () => { remove(); resolve(); };
        const failed = () => { remove(); reject(new Error('PDF SDK load failed')); };
        documentViewer.addEventListener('documentLoaded', loaded);
        instance.UI.addEventListener?.('loaderror', failed);
        try {
          void Promise.resolve(instance.UI.loadDocument(blob, {
            extension: source.file_type || 'pdf', filename: source.filename || 'document.pdf',
          })).catch(failed);
        } catch { failed(); }
      });
      if (!current()) return;
      await documentViewer.getAnnotationsLoadedPromise();
      if (!current()) return;
      const document = documentViewer.getDocument();
      if (!document) throw new Error('PDF document unavailable');

      const prior = draftsRef.current.get(key);
      if (prior) await waitForRequest(prior.queue.flush().catch(() => {}));
      if (!current()) return;
      let unsavedDraft: string | undefined;
      try {
        const token = await waitForRequest(tokenForOwner());
        const saved = await waitForRequest(annotationsApi.getAnnotations(source.id, token));
        if (!current() || documentViewer.getDocument() !== document) return;
        unsavedDraft = prior?.queue.getState().pending ? prior.xfdf : undefined;
        const xfdf = unsavedDraft ?? saved.xfdf;
        if (xfdf) {
          // XFDF is a full snapshot, including deletion of every annotation.
          // Remove embedded originals before importing the saved snapshot.
          annotationManager.deleteAnnotations(annotationManager.getAnnotationsList(), { imported: true, force: true });
          await annotationManager.importAnnotations(xfdf);
          if (!current() || documentViewer.getDocument() !== document) return;
        }
      } catch (error) {
        if (!current() || documentViewer.getDocument() !== document) return;
        // A loaded document remains readable when its separate annotation
        // request/import fails. Do not create a save session or enable editing:
        // a new snapshot could replace saved marks that were not restored.
        const legacy = (error as { data?: { code?: string } } | null)?.data?.code === 'ANNOTATION_LEGACY_UNSUPPORTED';
        setStatus(legacy ? 'legacy-readonly' : 'annotations-load-error');
        onReadyRef.current(instance);
        onLoadingChange(false);
        return;
      }

      queue = createAnnotationSaveQueue({
        save: async snapshot => {
          const freshToken = await tokenForOwner();
          await annotationsApi.saveAnnotations(source.id, snapshot, freshToken);
        },
        onState: state => {
          if (current()) setStatus(exportFailed ? 'error'
            : state.status === 'saved' && capturesPending > 0 ? 'unsaved' : state.status);
        },
      });
      const draft: Draft = { queue };
      draftsRef.current.set(key, draft);
      const ownedQueue = queue;
      const capture = () => {
        // Capture the actual objects before awaiting token/export work. The next
        // load waits for this export chain, so these remain this document's marks.
        if (documentViewer.getDocument() !== document) return Promise.reject(new Error('PDF document changed'));
        const annotations = annotationManager.getAnnotationsList().filter(annotation => annotation !== readAlongHighlight.current);
        capturesPending++;
        pendingExports.current++;
        if (current()) setStatus('unsaved');
        const result = exports.then(async () => {
          if (documentViewer.getDocument() !== document) throw new Error('PDF document changed');
          const snapshot = await annotationManager.exportAnnotations({ annotList: annotations });
          exportFailed = false;
          draft.xfdf = snapshot;
          ownedQueue.enqueue(snapshot);
          return snapshot;
        });
        exports = result.then(() => {
          capturesPending--;
          pendingExports.current--;
          if (current() && capturesPending === 0) setStatus(ownedQueue.getState().status);
        }, () => {
          exportFailed = true;
          capturesPending--;
          pendingExports.current--;
          if (current()) setStatus('error');
        });
        return result;
      };
      captureForRecovery = capture;
      const changed = (_annotations: unknown[], _action: string, info?: { imported?: boolean; source?: string }) => {
        if (!current() || !ready || info?.imported || info?.source === 'read-along') return;
        setStatus('unsaved');
        void capture().catch(() => {});
      };
      annotationManager.addEventListener('annotationChanged', changed);
      saveHandler = () => annotationManager.removeEventListener('annotationChanged', changed);
      sessionRef.current = { capture, queue, document };
      ready = true;
      annotationManager.disableReadOnlyMode();
      onReadyRef.current(instance);
      setStatus('saved');
      if (unsavedDraft !== undefined) {
        draft.xfdf = unsavedDraft;
        ownedQueue.enqueue(unsavedDraft);
      }
      onLoadingChange(false);
    }).catch(() => {
      if (!current()) return;
      setStatus('load-error');
      onLoadingChange(false);
    });
    loadBarrier.current = load;

    return () => {
      active = false;
      annotationManager.enableReadOnlyMode();
      ready = false;
      abort.abort();
      saveHandler?.();
      sessionRef.current = null;
      // Already captured snapshots retain their original document/user binding.
      // Detach after exports enqueue them; pending writes then drain silently.
      const cleanup = Promise.allSettled([load, loadBarrier.current]).then(() => exports).then(() => {
        if (exportFailed && captureForRecovery && queue && ownerRef.current === owner) {
          blockedSnapshot.current = { owner, capture: captureForRecovery, queue };
        } else queue?.detach();
      });
      loadBarrier.current = cleanup;
    };
  }, [instance, material?.id, userId, reload, onLoadingChange, readAlongHighlight]);

  useEffect(() => {
    const warnUnsaved = (event: BeforeUnloadEvent) => {
      if (!blockedSnapshot.current && pendingExports.current === 0 && status !== 'unsaved' && status !== 'saving' && status !== 'error'
        && ![...draftsRef.current.values()].some(draft => draft.queue.getState().pending)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnUnsaved);
    return () => window.removeEventListener('beforeunload', warnUnsaved);
  }, [status]);

  const saveNow = useCallback(async () => {
    const session = sessionRef.current;
    if (!session) throw new Error('Annotations are not ready');
    await session.capture();
    await session.queue.retry();
  }, []);

  const exportPdf = useCallback(async () => {
    const session = sessionRef.current;
    const source = materialRef.current;
    if (!session || !source) throw new Error('Annotations are not ready');
    const xfdf = await session.capture();
    if (sessionRef.current !== session) throw new Error('PDF document changed');
    const bytes = session.document.getFileData({ xfdfString: xfdf, downloadType: 'pdf', flatten: false });
    // Document replacement waits for PDF export as well as XFDF snapshots.
    loadBarrier.current = Promise.allSettled([loadBarrier.current, bytes]);
    const data = await bytes;
    if (sessionRef.current !== session) throw new Error('PDF document changed');
    return data;
  }, []);

  return { status, saveNow, exportPdf, retryLoad: () => setReload(value => value + 1) };
}
